import React from "react";
import Modal from "../Modal";
import { RSButton } from "reactsymbols-kit";
import Styles from "../../styles/colors";
import { confirmable, type ConfirmDialogProps } from "react-confirm";

/**
 * The caller-supplied options for the dialog. Only `title`, `cancelText` and
 * `proceedText` are read here; all are optional (the buttons fall back to
 * "Cancel" / "Continue").
 */
export interface ConfirmationOptions {
  title?: React.ReactNode;
  cancelText?: string;
  proceedText?: string;
}

/** The caller-supplied props (the `P` half of react-confirm's dialog props). */
export interface ConfirmationDialogOwnProps {
  confirmation: React.ReactNode;
  options: ConfirmationOptions;
}

// react-confirm resolves with no value (the buttons call `proceed()` with no
// argument), so the resolved type is `void`.
type ConfirmationDialogProps = ConfirmDialogProps<ConfirmationDialogOwnProps, void>;

/**
 * Confirmation dialog using react-confirm:
 * https://github.com/haradakunihiko/react-confirm
 *
 * Props: see the documentation of react-confirm. The following options are allowed:
 * - title: title for the confirmation dialog
 * - proceedText: the text for the positive button
 */
const ConfirmationDialog = ({
  show,
  proceed,
  dismiss,
  cancel,
  confirmation,
  options
}: ConfirmationDialogProps) => {
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    proceed();
  };

  return (
    <Modal isOpen={show} onRequestClose={cancel} onEnter={proceed}>
      <div className="merlin-modal">
        <form onSubmit={onSubmit}>
          {options.title ? <div className="title">{options.title}</div> : null}
          <div className="content p-2.5">{confirmation}</div>
          <div className="button-wrapper">
            <RSButton
              type="button"
              value={options.cancelText || "Cancel"}
              background={Styles.defaultGradient}
              color={Styles.darkTextColor}
              onClick={cancel}
            />
            <RSButton
              type="submit"
              autoFocus={true}
              value={options.proceedText || "Continue"}
              background={Styles.blueGradient}
            />
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default confirmable(ConfirmationDialog);
